import React from 'react';
import { Link } from 'react-router-dom';
import { Grid, Typography, colors } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { createStyles } from 'app/util/styles';
import { Button } from 'app/components/Button';
import { HeroImage } from 'app/components/Pages/Templates/HeroImage';
import { AssistedForm } from 'app/components/AssistedForm';
import { routes } from 'app/routes/route-list';
import { getImagePathSafely } from 'app/util/images';

/**
 * Props for the full-screen mobile background wrapper.
 */
interface MobileBackgroundProps {
  /** Content rendered on top of the background image. */
  children: React.ReactNode;
  /** Resolved URL of the background image, consumed by the styles below. */
  imagePath: string;
}

/**
 * Styles for the full-screen mobile background. The image URL arrives via props (tss-react passes the
 * component's props to the styles callback), so the styled component can be created once at module scope.
 *
 * @param {any} theme - MUI theme.
 * @param {MobileBackgroundProps} props - Component props; only `imagePath` is read.
 * @returns {object} The `backgroundImage` rule.
 */
const mobileBackgroundStyles = (theme, { imagePath }: MobileBackgroundProps) =>
  createStyles({
    backgroundImage: {
      [theme.breakpoints.down('md')]: {
        background: `url('${imagePath}') no-repeat 50% center / cover`,
        maxWidth  : '100%',
        width     : '100%',
        height    : '100%',
        padding   : '0 16px',
        position  : 'fixed',
        top       : 0,
        left      : 0,
        '& .branchSubheading': {
          color     : colors.common.white,
          textShadow: '0 2px 5px rgba(0,0,0,0.5)',
          marginTop : '10px',
          lineHeight: 1.2,
          padding   : 0,
        },
      },
      [theme.breakpoints.between('sm', 'md')]: {
        '& .branchSubheading': {
          fontSize: 30,
        },
        '& .appButton': {
          fontSize    : 25,
          fontWeight  : 500,
          minWidth    : '375px',
          minHeight   : '75px',
          padding     : '0 16px',
          marginTop   : '20px',
        },
      },
    },
  });

/**
 * Lays the branch content over a full-screen background image on small screens.
 *
 * @param {object} props - Component props.
 * @param {Record<string, string>} props.classes - Class names generated from {@link mobileBackgroundStyles}.
 * @param {React.ReactNode} props.children - Content rendered on top of the background.
 * @returns {JSX.Element} The background container.
 */
const MobileBackground = ({ classes, children }: MobileBackgroundProps & { classes: Record<string, string> }) => {
  return (
    <Grid
      container
      direction = 'column'
      justifyContent   = 'center'
      className = {classes.backgroundImage}
    >
      <Grid item>{children}</Grid>
    </Grid>
  );
};

const MobileWrapper: any = withStyles(MobileBackground, mobileBackgroundStyles);

/**
 * Wraps the branch page in the full-screen mobile background when enabled; otherwise renders the children as-is.
 *
 * Declared at module scope (with `MobileWrapper` above) so component identity is stable across renders. When both
 * were declared inside `BranchPage`, every re-render created new component types and React remounted the whole
 * page, collapsing its height and jumping the scroll position.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The branch page content.
 * @param {string} props.imagePath - Resolved background image URL, used only when `showFullScreenImage` is set.
 * @param {boolean} props.showFullScreenImage - Whether to render the full-screen mobile background.
 * @returns {JSX.Element} The wrapped or unwrapped content.
 */
const FullScreenImageWrapper = (
  { children, imagePath, showFullScreenImage }: { children: React.ReactNode; imagePath: string; showFullScreenImage: boolean },
) => {
  return showFullScreenImage
    ? <MobileWrapper imagePath={imagePath}>{children}</MobileWrapper>
    : <React.Fragment>{children}</React.Fragment>;
};

export const BranchPage = ({ ...props }: any) => {
  const {
    buttons             = [],
    heroImageSrc        = '',
    heroImageTitle      = '',
    paragraph           = '',
    showAssistedForm    = false,
    showContactLO       = false,
    showFullScreenImage = false,
  } = props;
  const buttonLayout        = `numberOfButtons-${buttons.length}`;
  const fullScreenImagePath = getImagePathSafely(heroImageSrc);

  return (
    <FullScreenImageWrapper imagePath={fullScreenImagePath} showFullScreenImage={showFullScreenImage}>
      <HeroImage
        heroImageClass  = 'branchHero'
        heroTitleClass  = 'branchTitle'
        image           = {heroImageSrc}
        pageTitle       = {heroImageTitle}
        removeFormContainer
        showMobileImage = {!showFullScreenImage}
        titleClass      = 'branchTitle heroTitle'
        heroGridProps   = {{
          alignItems: 'flex-start',
          direction : 'column',
        }}
      >
        <Grid
          container
          alignContent = 'flex-start'
          classes      = {{ container: 'branchContainer branchContent' }}
        >
          <Grid item xs={12}>
            <Typography variant='subtitle1' classes={{ subtitle1: 'branchSubheading' }}>
              {paragraph}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              justifyContent = 'space-between'
              classes = {{ container: `branchButtonContainer ${buttonLayout}` }}
            >
              {buttons.map((button, index) => {
                return (
                  <Grid item key={index} classes={{ item: 'gridButton' }}>
                    <Button
                      classes   = {{ root: 'tabletButton appButton' }}
                      color     = 'primary'
                      component = {Link}
                      to        = {button.url}
                      variant   = 'contained'
                      onClick   = {button.handleClick}
                    >
                      {button.label}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
        {showAssistedForm && (<AssistedForm {...props} />)}
        {showContactLO && (
          <Grid container classes={{ container: 'lightBackground banner' }}>
            <Grid item xs={12}>
              <Button
                classes   = {{ root: 'tabletButton' }}
                color     = 'secondary'
                component = {Link}
                to        = {routes.contact}
                variant   = 'contained'
              >
                Contact Loan Officer
              </Button>
            </Grid>
          </Grid>
        )}
      </HeroImage>
    </FullScreenImageWrapper>
  );
};
